import type { DemandPointModel } from '@/shared/generated'

import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	//ReferenceLine,
	XAxis,
	YAxis,
} from 'recharts'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { buildDemandBarRows } from '../lib/demand-bars'
import { formatDate } from '../lib/period'

const formatTick = (date: string) => format(parseISO(date), 'd MMM')

const formatTooltipDate = (value: unknown) =>
	typeof value === 'string' ? formatDate(value) : ''

export const DemandBarsChart = ({
	points,
	windowStartIndex,
	height = 40,
	variant = 'spark',
	'data-testid': testId,
}: {
	points: DemandPointModel[]
	windowStartIndex?: number
	height?: number
	variant?: 'spark' | 'detailed'
	'data-testid'?: string
}) => {
	const { t } = useTranslation()
	const rows = buildDemandBarRows(points, windowStartIndex)
	const detailed = variant === 'detailed'
	/* const dividerDate =
		detailed && windowStartIndex !== undefined
			? points[windowStartIndex]?.date_on
			: undefined */

	const unitsLabel = t('midterm_forecast.tooltip_units', {
		defaultValue: 'Units',
	})
	const chartConfig = { units: { label: unitsLabel, color: 'var(--primary)' } }

	return (
		<ChartContainer
			config={chartConfig}
			className={cn('w-full', !detailed && 'aspect-auto')}
			style={{ height }}
			data-testid={testId}
		>
			<BarChart
				accessibilityLayer={false}
				data={rows}
				margin={
					detailed
						? { top: 8, right: 4, bottom: 0, left: 0 }
						: { top: 2, right: 0, bottom: 0, left: 0 }
				}
				barCategoryGap='20%'
			>
				{detailed && <CartesianGrid vertical={false} strokeDasharray='3 3' />}
				{detailed && (
					<XAxis
						dataKey='date_on'
						tickFormatter={formatTick}
						minTickGap={32}
						tickLine={false}
						axisLine={false}
						fontSize={10}
					/>
				)}
				{detailed && (
					<YAxis
						width={30}
						tickLine={false}
						axisLine={false}
						allowDecimals={false}
						fontSize={10}
					/>
				)}
				{detailed && (
					<ChartTooltip
						cursor={{ fill: 'var(--muted)', fillOpacity: 0.4 }}
						content={
							<ChartTooltipContent
								hideIndicator
								labelFormatter={formatTooltipDate}
								formatter={(value) => (
									<div className='flex w-full items-center justify-between gap-2 leading-none'>
										<span className='text-background/50'>{unitsLabel}</span>
										<span className='font-mono font-medium tabular-nums'>
											{formatNumber(Number(value), NumberFormats.Integer)}
										</span>
									</div>
								)}
							/>
						}
					/>
				)}
				{/* {dividerDate && (
					<ReferenceLine
						x={dividerDate}
						stroke='var(--muted-foreground)'
						strokeDasharray='2 2'
					/>
				)} */}
				<Bar
					dataKey='units'
					name='units'
					minPointSize={2}
					radius={[1, 1, 0, 0]}
					isAnimationActive={false}
				>
					{rows.map((row) => (
						<Cell
							key={row.date_on}
							fill={row.fill}
							fillOpacity={row.fillOpacity}
						/>
					))}
				</Bar>
			</BarChart>
		</ChartContainer>
	)
}
